/**
 * Screen 5 — Member profile.
 *
 * Renew and Record payment are the two things staff come here to do, so they
 * sit at the top, full size, before any history. Everything below is the
 * record: every term ever sold and every payment ever taken.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { loadMemberProfile, type SubscriptionRow } from '../db/queries';
import { StatusBadge, BalanceDueBadge, CopyPhoneButton } from '../components/StatusBadge';
import RecordPaymentDialog from '../components/RecordPaymentDialog';
import FreezeDialog from '../components/FreezeDialog';
import CancelDialog from '../components/CancelDialog';
import VoidDialog from '../components/VoidDialog';
import { cancelFreeze } from '../db/operations';
import { describeAllowance } from '@shared/freezes';
import {
  describeCancellationWindow,
  isWithinCancellationWindow,
  sessionsInTerm,
} from '@shared/cancellations';
import { classLabel } from '@shared/catalog';
import { formatEgp } from '@shared/money';
import { formatDisplayDate, todayISO } from '@shared/dates';
import { formatPhone } from '@shared/identity';
import { describeRemaining } from '@shared/pricing';
import { PAYMENT_METHOD_LABELS } from '@shared/types';

export default function MemberProfile() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [payingFor, setPayingFor] = useState<SubscriptionRow | null>(null);
  const [freezingFor, setFreezingFor] = useState<SubscriptionRow | null>(null);
  const [cancellingFor, setCancellingFor] = useState<SubscriptionRow | null>(null);
  const [voidingFor, setVoidingFor] = useState<SubscriptionRow | null>(null);
  const today = todayISO();

  const profile = useLiveQuery(() => loadMemberProfile(id, today), [id, today], undefined);

  if (profile === undefined) {
    return <p className="py-20 text-center text-white/30">Loading…</p>;
  }

  if (profile === null) {
    return (
      <div className="mx-auto max-w-lg px-5 py-20 text-center">
        <h1 className="mb-3 text-3xl text-brand">Member not found</h1>
        <Link to="/members" className="btn mt-4">
          Back to members
        </Link>
      </div>
    );
  }

  const { member, subscriptions, payments, totalOutstanding } = profile;
  // The term staff will act on: the one running furthest ahead, else the newest.
  const primary = subscriptions.find((s) => s.status !== 'cancelled') ?? subscriptions[0] ?? null;

  return (
    <div className="mx-auto max-w-4xl px-5 py-6">
      <Link
        to="/members"
        className="no-print mb-4 inline-block text-sm font-semibold text-white/40 hover:text-brand"
      >
        ← Members
      </Link>

      {/* Identity */}
      <div className="card mb-4 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="tabular mb-1 text-2xl font-bold text-brand">{member.sk_id}</div>
            <h1 className="mb-2 text-3xl text-paper" dir="auto">
              {member.full_name}
            </h1>
            <div className="flex flex-wrap items-center gap-3">
              <span className="tabular text-white/50">{formatPhone(member.phone)}</span>
              {member.phone && <CopyPhoneButton phone={member.phone} />}
            </div>
            {member.notes && (
              <p className="mt-3 max-w-lg text-sm text-white/40" dir="auto">
                {member.notes}
              </p>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            {primary && <StatusBadge status={primary.status} />}
            <BalanceDueBadge remaining={totalOutstanding} />
          </div>
        </div>
      </div>

      {/* The two actions */}
      <div className="no-print mb-6 flex flex-wrap gap-3">
        {primary ? (
          <button
            onClick={() => navigate(`/renew/${primary.subscription.id}`)}
            className="btn flex-1"
          >
            ↻ Renew
          </button>
        ) : (
          <Link to="/new" className="btn flex-1">
            + Add subscription
          </Link>
        )}
        <button
          onClick={() => setPayingFor(subscriptions.find((s) => s.remaining > 0) ?? primary)}
          disabled={!primary}
          className="btn-ghost flex-1"
        >
          Record payment
        </button>
        <button
          onClick={() => primary && setFreezingFor(primary)}
          disabled={!primary || !primary.freezesLeft}
          title={
            primary && !primary.freezesLeft
              ? describeAllowance(primary.subscription.months)
              : undefined
          }
          className="btn-ghost flex-1"
        >
          ❄ Freeze
        </button>
      </div>

      {/* Subscription history */}
      <section className="mb-6">
        <h2 className="mb-3 text-xl text-brand">Subscriptions</h2>
        {subscriptions.length === 0 ? (
          <p className="card p-8 text-center text-white/40">No subscriptions yet</p>
        ) : (
          <ul className="space-y-2">
            {subscriptions.map((row) => (
              <li key={row.subscription.id} className="card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold">
                      {classLabel(row.subscription.class_key)}
                      {row.subscription.private && (
                        <span className="ml-2 rounded-btn border border-gold/40 bg-gold/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-button text-gold">
                          Private
                        </span>
                      )}
                    </div>
                    {row.subscription.private && row.subscription.private_coach && (
                      <div className="text-sm text-gold/70" dir="auto">
                        with {row.subscription.private_coach}
                      </div>
                    )}
                    <div className="text-sm text-white/40">
                      {formatDisplayDate(row.subscription.start_date)} →{' '}
                      {formatDisplayDate(row.subscription.end_date)} · {row.subscription.months}{' '}
                      {row.subscription.months === 1 ? 'month' : 'months'}
                    </div>
                    <div className="mt-1 text-sm text-white/30">
                      {describeRemaining(row.daysRemaining)}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="tabular text-lg font-bold">
                      {formatEgp(row.subscription.total_due_piastres)}
                    </div>
                    <div className="tabular text-sm text-white/40">
                      Paid {formatEgp(row.amountPaid, { hideZeroFraction: true })}
                    </div>
                    <div className="mt-2 flex flex-wrap justify-end gap-2">
                      <StatusBadge status={row.status} size="sm" />
                      <BalanceDueBadge remaining={row.remaining} size="sm" />
                    </div>
                  </div>
                </div>

                {/* Freeze history and what is left of the allowance. */}
                {(row.freezes.length > 0 || row.freezesLeft > 0) && (
                  <div className="mt-3 border-t border-edge/50 pt-3">
                    {row.frozenUntil && (
                      <div className="mb-2 rounded-btn border border-status-frozen/40 bg-status-frozenBg p-2 text-sm font-semibold text-status-frozen">
                        Frozen until {formatDisplayDate(row.frozenUntil)}
                      </div>
                    )}
                    {row.freezes.map((f) => (
                      <div key={f.id} className="flex items-center justify-between gap-3 py-1 text-sm">
                        <span className="text-white/50">
                          ❄ {formatDisplayDate(f.start_date)} → {formatDisplayDate(f.end_date)}
                          <span className="tabular ml-2 text-white/30">{f.days} days</span>
                          {f.reason && <span className="ml-2 text-white/30">· {f.reason}</span>}
                        </span>
                        <button
                          onClick={() => cancelFreeze(f.id)}
                          className="no-print shrink-0 rounded-btn border border-edge px-2 py-1 text-[10px] font-bold uppercase tracking-button text-white/40 hover:border-status-expired hover:text-status-expired"
                          title="Undo this freeze and pull the days back off the end date"
                        >
                          Undo
                        </button>
                      </div>
                    ))}
                    <div className="mt-1 text-xs text-white/25">
                      {describeAllowance(row.subscription.months)}
                      {row.freezesLeft > 0
                        ? ` · ${row.freezesLeft} left`
                        : ' · fully used'}
                    </div>
                  </div>
                )}

                {row.remaining > 0 && (
                  <button
                    onClick={() => setPayingFor(row)}
                    className="no-print mt-3 w-full rounded-btn border border-status-due/40
                               bg-status-dueBg py-2 text-sm font-bold uppercase tracking-button
                               text-status-due hover:bg-status-due/20"
                  >
                    Take {formatEgp(row.remaining, { hideZeroFraction: true })}
                  </button>
                )}

                {/* What happened, for a subscription that was cancelled. */}
                {row.subscription.cancelled && (
                  <div className="mt-3 rounded-btn border border-status-cancelled/40 bg-status-cancelledBg p-3 text-sm text-status-cancelled">
                    <div className="font-bold">
                      Cancelled
                      {row.subscription.cancelled_at &&
                        ` on ${formatDisplayDate(row.subscription.cancelled_at)}`}
                    </div>
                    {row.subscription.sessions_attended !== null && (
                      <div className="mt-1 text-white/50">
                        {row.subscription.sessions_attended} of{' '}
                        {sessionsInTerm(row.subscription.months)} sessions attended ·{' '}
                        {formatEgp(row.subscription.refund_piastres ?? 0, {
                          hideZeroFraction: true,
                        })}{' '}
                        refunded
                      </div>
                    )}
                  </div>
                )}

                {/*
                  Cancelling is only offered inside the 14-day window. Outside
                  it the button is gone rather than disabled — there is nothing
                  staff can do to make it work, so offering it would only invite
                  the question.
                */}
                {!row.subscription.cancelled &&
                  isWithinCancellationWindow(row.subscription.start_date, today) && (
                    <button
                      onClick={() => setCancellingFor(row)}
                      title={describeCancellationWindow(row.subscription.start_date, today)}
                      className="no-print mt-3 w-full rounded-btn border border-edge py-2 text-xs
                                 font-bold uppercase tracking-button text-white/40
                                 hover:border-status-expired hover:text-status-expired"
                    >
                      Cancel &amp; refund ·{' '}
                      {describeCancellationWindow(row.subscription.start_date, today)}
                    </button>
                  )}

                {/*
                  Rarer and more destructive than Cancel — it erases the sale
                  rather than winding it down — so it is deliberately smaller
                  and dimmer, not sitting next to Cancel as an equal choice.
                */}
                <button
                  onClick={() => setVoidingFor(row)}
                  className="no-print mt-2 w-full text-center text-[11px] text-white/20
                             hover:text-status-expired"
                >
                  Void (entered by mistake)
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Payment ledger */}
      <section>
        <h2 className="mb-3 text-xl text-brand">Payments</h2>
        {payments.length === 0 ? (
          <p className="card p-8 text-center text-white/40">Nothing paid yet</p>
        ) : (
          <ul className="card divide-y divide-edge">
            {payments.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-4 p-4">
                <div>
                  <div className="tabular font-semibold">{formatDisplayDate(p.paid_at)}</div>
                  <div className="text-sm text-white/40">
                    {PAYMENT_METHOD_LABELS[p.method]}
                    {p.received_by ? ` · ${p.received_by}` : ''}
                  </div>
                </div>
                <div
                  className={`tabular text-lg font-bold ${
                    p.amount_piastres < 0 ? 'text-status-expired' : ''
                  }`}
                >
                  {formatEgp(p.amount_piastres)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {payingFor && (
        <RecordPaymentDialog row={payingFor} onClose={() => setPayingFor(null)} />
      )}
      {freezingFor && (
        <FreezeDialog row={freezingFor} onClose={() => setFreezingFor(null)} />
      )}
      {cancellingFor && (
        <CancelDialog row={cancellingFor} onClose={() => setCancellingFor(null)} />
      )}
      {voidingFor && (
        <VoidDialog row={voidingFor} onClose={() => setVoidingFor(null)} />
      )}
    </div>
  );
}
